<template>
  <div class="ocr-editor">
    <div class="ocr-editor-header">
      <div class="header-left">
        <router-link to="/integrations/page/protokol-media/ocr-models" class="back-link">
          <i class="fa-solid fa-arrow-left"></i>
        </router-link>
        <h2 class="header-title">{{ model ? model.name : $t('integrations.media.ocr.editorTitle') }}</h2>
      </div>
      <div class="header-actions">
        <button class="btn-save" @click="saveModel" :disabled="saving || (form.engine === 'kortex' && !isKortexAvailable)">
          <i class="fa-solid fa-floppy-disk"></i>
          {{ saving ? $t('integrations.media.ocr.saving') : $t('integrations.media.ocr.save') }}
        </button>
      </div>
    </div>

    <div class="ocr-editor-body">
      <!-- Left Panel -->
      <div class="left-panel">
        <div class="panel-section">
          <label class="section-label">{{ $t('integrations.media.ocr.name') }}</label>
          <input v-model="form.name" type="text" class="form-input" />
        </div>

        <div class="panel-section">
          <label class="section-label">{{ $t('integrations.media.ocr.description') }}</label>
          <input v-model="form.description" type="text" class="form-input" :placeholder="$t('integrations.media.ocr.optional')" />
        </div>

        <div class="panel-section">
          <label class="section-label">{{ $t('integrations.media.ocr.engine') }}</label>
          <select v-model="form.engine" class="form-input">
            <option value="default">{{ $t('integrations.media.ocr.engineDefault') }}</option>
            <option value="kortex">{{ $t('integrations.media.ocr.engineKortex') }}</option>
          </select>
          <div v-if="form.engine === 'kortex' && !isKortexAvailable" class="engine-gate">
            <i class="fa-solid fa-lock"></i>
            <div class="engine-gate-text">
              <span class="engine-gate-title">{{ $t('integrations.media.ocr.kortexGateTitle') }}</span>
              <span class="engine-gate-desc">{{ $t('integrations.media.ocr.kortexGateDesc') }}</span>
            </div>
            <router-link
              :to="{ name: 'marketplace.List', query: { open: 'protokol-kortex' } }"
              class="engine-gate-btn"
            >
              <i class="fa-solid fa-store"></i> {{ $t('integrations.media.ocr.kortexGateBtn') }}
            </router-link>
          </div>
        </div>

        <div v-if="form.engine === 'default'" class="panel-section">
          <label class="section-label">{{ $t('integrations.media.ocr.language') }}</label>
          <v-select
            v-model="selectedLanguage"
            :options="languageOptions"
            label="name"
            :reduce="opt => opt.code"
            placeholder="Auto-detect"
            :clearable="true"
            class="lang-select"
          />
        </div>

        <div v-if="form.engine === 'default'" class="panel-section">
          <label class="section-label">{{ $t('integrations.media.ocr.psm') }}</label>
          <div class="psm-list">
            <label
              v-for="opt in psmOptions"
              :key="opt.value"
              :class="['psm-option', { active: form.psm === opt.value }]"
            >
              <input type="radio" :value="opt.value" v-model="form.psm" />
              <div class="psm-option-content">
                <span class="psm-option-name">{{ $t('integrations.media.ocr.' + opt.nameKey) }}</span>
                <span class="psm-option-desc">{{ $t('integrations.media.ocr.' + opt.descKey) }}</span>
              </div>
            </label>
          </div>
        </div>

        <div class="panel-section">
          <label class="section-label">{{ $t('integrations.media.ocr.drawingMode') }}</label>
          <div class="mode-toggle">
            <button
              :class="['mode-btn', { active: drawMode === 'boundary' }]"
              @click="drawMode = 'boundary'"
            >
              <i class="fa-solid fa-expand"></i>
              {{ $t('integrations.media.ocr.boundary') }}
            </button>
            <button
              :class="['mode-btn', { active: drawMode === 'region' }]"
              @click="drawMode = 'region'"
              :disabled="!currentSampleBoundary"
            >
              <i class="fa-solid fa-vector-square"></i>
              {{ $t('integrations.media.ocr.region') }}
            </button>
          </div>
        </div>

        <!-- Samples -->
        <div class="panel-section">
          <label class="section-label">{{ $t('integrations.media.ocr.sampleImages') }}</label>
          <div class="samples-list">
            <div
              v-for="(sample, idx) in samples"
              :key="sample.uuid || idx"
              :class="['sample-thumb', { active: activeSampleIdx === idx }]"
              @click="activeSampleIdx = idx"
            >
              <img :src="getSampleUrl(sample)" />
              <span v-if="sample.is_primary" class="primary-badge">
                <i class="fa-solid fa-star"></i>
              </span>
              <button class="sample-remove" @click.stop="removeSample(idx)">
                <i class="fa-solid fa-xmark"></i>
              </button>
            </div>
            <label class="sample-add">
              <input type="file" accept="image/*" multiple @change="onAddSamples" hidden />
              <i class="fa-solid fa-plus"></i>
            </label>
          </div>
        </div>

        <!-- Regions -->
        <div class="panel-section">
          <label class="section-label">{{ $t('integrations.media.ocr.region') }} ({{ regions.length }})</label>
          <div class="regions-list">
            <div
              v-for="(region, idx) in regions"
              :key="idx"
              :class="['region-item', { active: selectedRegionIdx === idx }]"
              @click="selectedRegionIdx = idx"
            >
              <div class="region-color" :style="{ background: regionColor(idx) }"></div>
              <input
                v-model="region.label"
                type="text"
                class="region-label-input"
                placeholder="Label..."
                @click.stop
              />
              <button class="region-remove" @click.stop="removeRegion(idx)">
                <i class="fa-solid fa-xmark"></i>
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Canvas Area -->
      <div class="canvas-area" ref="canvasArea">
        <div v-if="samples.length === 0" class="canvas-placeholder">
          <label class="upload-zone">
            <input type="file" accept="image/*" multiple @change="onAddSamples" hidden />
            <i class="fa-solid fa-cloud-arrow-up"></i>
            <span>{{ $t('integrations.media.ocr.uploadSamples') }}</span>
          </label>
        </div>
        <div v-else class="canvas-wrapper" ref="canvasWrapper">
          <img
            ref="sampleImage"
            :src="getSampleUrl(samples[activeSampleIdx])"
            class="sample-img"
            @load="onImageLoad"
            draggable="false"
          />
          <canvas
            v-show="drawMode === 'region'"
            ref="overlayCanvas"
            class="overlay-canvas"
            @mousedown="onCanvasMouseDown"
            @mousemove="onCanvasMouseMove"
            @mouseup="onCanvasMouseUp"
          ></canvas>
        </div>
      </div>
    </div>

    <!-- Label Input Dialog -->
    <div v-if="pendingLabel" class="dialog-overlay" @click.self="cancelLabel">
      <div class="dialog dialog-small">
        <h3 class="dialog-title">{{ $t('integrations.media.ocr.regionLabel') }}</h3>
        <div class="dialog-field">
          <input
            v-model="pendingLabel.label"
            type="text"
            :placeholder="$t('integrations.media.ocr.regionLabelPlaceholder')"
            ref="labelInput"
            @keyup.enter="confirmLabel"
          />
        </div>
        <div class="dialog-actions">
          <button class="btn-cancel" @click="cancelLabel">{{ $t('integrations.media.ocr.cancel') }}</button>
          <button class="btn-confirm" @click="confirmLabel" :disabled="!pendingLabel.label">{{ $t('integrations.media.ocr.add') }}</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Cropper from 'cropperjs'
import 'cropperjs/dist/cropper.min.css'
import vSelect from 'vue-select'
import { OCR_LANGUAGES } from './ocrLanguages'

const REGION_COLORS = [
  '#ef4444', '#f97316', '#eab308', '#22c55e', '#06b6d4',
  '#3b82f6', '#8b5cf6', '#ec4899', '#f43f5e', '#14b8a6',
]

export default {
  components: { vSelect },
  props: {
    context: { type: Object },
  },

  data() {
    return {
      form: { name: '', description: '', engine: 'default', language: '', psm: null },
      languageOptions: OCR_LANGUAGES,
      psmOptions: [
        { value: null, nameKey: 'psmAuto', descKey: 'psmAutoDesc' },
        { value: 6, nameKey: 'psmUniformBlock', descKey: 'psmUniformBlockDesc' },
        { value: 7, nameKey: 'psmSingleLine', descKey: 'psmSingleLineDesc' },
        { value: 8, nameKey: 'psmSingleWord', descKey: 'psmSingleWordDesc' },
        { value: 4, nameKey: 'psmSingleColumn', descKey: 'psmSingleColumnDesc' },
        { value: 11, nameKey: 'psmSparseText', descKey: 'psmSparseTextDesc' },
        { value: 13, nameKey: 'psmRawLine', descKey: 'psmRawLineDesc' },
      ],
      samples: [],
      regions: [],
      activeSampleIdx: 0,
      selectedRegionIdx: -1,
      drawMode: 'boundary',
      drawing: false,
      drawStart: null,
      drawCurrent: null,
      pendingLabel: null,
      saving: false,
      model: null,
      imageLoaded: false,
      cropper: null,
    }
  },

  computed: {
    selectedLanguage: {
      get() { return this.form.language || null },
      set(val) { this.form.language = val || '' },
    },
    currentSampleBoundary() {
      const sample = this.samples[this.activeSampleIdx]
      return sample && sample.document_boundary
    },
    isKortexAvailable() {
      return this.$store.getters['integrations/isIntegrationActive']('protokol-kortex')
    },
  },

  async mounted() {
    const plan = await this.$store.dispatch('ocrModels/fetchPlan')
    if (!plan || plan.plan !== 'paid') {
      this.$router.replace('/integrations/page/protokol-media/page')
      return
    }

    const uuid = this.$route.query.uuid
    if (uuid) {
      const model = await this.$store.dispatch('ocrModels/fetchModel', uuid)
      this.model = model
      this.form.name = model.name
      this.form.description = model.description
      this.form.engine = model.engine
      this.form.language = model.language || ''
      this.form.psm = (model.config && model.config.psm != null) ? model.config.psm : null
      this.regions = (model.regions || []).map(r => ({ ...r }))
      this.samples = (model.samples || []).map(s => ({ ...s }))
    }
  },

  beforeDestroy() {
    this.destroyCropper()
  },

  methods: {
    getSampleUrl(sample) {
      if (!sample) return ''
      if (sample._localUrl) return sample._localUrl
      if (sample.image_url) return sample.image_url
      return ''
    },

    regionColor(idx) {
      return REGION_COLORS[idx % REGION_COLORS.length]
    },

    async uploadMediaFile(file, path, replace = false) {
      const formData = new FormData()
      formData.append('file', file)
      formData.append('path', path)
      formData.append('ref', '')
      if (replace) formData.append('replace', 'true')

      const resp = await this.$mediaHttp.post('/v3/dms/media/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })

      return Array.isArray(resp.data) ? resp.data[0] : resp.data
    },

    async uploadWithReplace(file, path) {
      try {
        return await this.uploadMediaFile(file, path)
      } catch (err) {
        const msg = err.response && err.response.data && err.response.data.message
        if (msg && msg.toLowerCase().includes('already exists')) {
          const replace = await this.$mainDialog.msgBoxConfirm(`File "${file.name}" already exists. Replace it?`)
          if (replace) {
            return await this.uploadMediaFile(file, path, true)
          }
          return null
        }
        throw err
      }
    },

    onAddSamples(e) {
      const files = Array.from(e.target.files)
      for (const file of files) {
        this.samples.push({
          _localUrl: URL.createObjectURL(file),
          _file: file,
          is_primary: this.samples.length === 0,
          image_key: '',
        })
      }
      if (this.activeSampleIdx < 0) this.activeSampleIdx = 0
      e.target.value = ''
    },

    async removeSample(idx) {
      const sample = this.samples[idx]
      if (sample.uuid && this.model) {
        await this.$store.dispatch('ocrModels/removeSample', {
          modelUuid: this.model.uuid,
          sampleUuid: sample.uuid,
        })
      }
      this.samples.splice(idx, 1)
      if (this.activeSampleIdx >= this.samples.length) {
        this.activeSampleIdx = Math.max(0, this.samples.length - 1)
      }
    },

    removeRegion(idx) {
      this.regions.splice(idx, 1)
      if (this.selectedRegionIdx >= this.regions.length) {
        this.selectedRegionIdx = -1
      }
      this.redrawCanvas()
    },

    onImageLoad() {
      this.imageLoaded = true
      this.$nextTick(() => {
        this.resizeCanvas()
        if (this.drawMode === 'boundary') {
          this.initCropper()
        }
      })
    },

    initCropper() {
      this.destroyCropper()
      const img = this.$refs.sampleImage
      if (!img) return

      const sample = this.samples[this.activeSampleIdx]
      const boundary = sample && sample.document_boundary

      this.cropper = new Cropper(img, {
        viewMode: 1,
        dragMode: 'crop',
        autoCrop: !!boundary,
        guides: true,
        center: true,
        highlight: true,
        background: false,
        responsive: true,
        restore: false,
        ready: () => {
          if (boundary) {
            const imageData = this.cropper.getImageData()
            this.cropper.setCropBoxData({
              left: imageData.left + boundary.x * imageData.naturalWidth * (imageData.width / imageData.naturalWidth),
              top: imageData.top + boundary.y * imageData.naturalHeight * (imageData.height / imageData.naturalHeight),
              width: boundary.width * imageData.width,
              height: boundary.height * imageData.height,
            })
          }
        },
        cropend: () => {
          this.saveBoundaryFromCropper()
        },
      })
    },

    destroyCropper() {
      if (this.cropper) {
        this.cropper.destroy()
        this.cropper = null
      }
    },

    saveBoundaryFromCropper() {
      if (!this.cropper) return
      const cropData = this.cropper.getData(true)
      const imageData = this.cropper.getImageData()

      const boundary = {
        x: cropData.x / imageData.naturalWidth,
        y: cropData.y / imageData.naturalHeight,
        width: cropData.width / imageData.naturalWidth,
        height: cropData.height / imageData.naturalHeight,
      }

      if (!this.samples[this.activeSampleIdx]) return
      this.$set(this.samples[this.activeSampleIdx], 'document_boundary', boundary)

      if (this.model && this.samples[this.activeSampleIdx].uuid) {
        this.$store.dispatch('ocrModels/updateSample', {
          modelUuid: this.model.uuid,
          sampleUuid: this.samples[this.activeSampleIdx].uuid,
          document_boundary: boundary,
        })
      }
    },

    resizeCanvas() {
      const img = this.$refs.sampleImage
      const canvas = this.$refs.overlayCanvas
      if (!img || !canvas) return

      canvas.width = img.clientWidth
      canvas.height = img.clientHeight
      this.redrawCanvas()
    },

    redrawCanvas() {
      const canvas = this.$refs.overlayCanvas
      if (!canvas) return
      const ctx = canvas.getContext('2d')
      const w = canvas.width
      const h = canvas.height

      ctx.clearRect(0, 0, w, h)

      // Draw document boundary
      const sample = this.samples[this.activeSampleIdx]
      if (sample && sample.document_boundary) {
        const b = sample.document_boundary
        ctx.strokeStyle = '#22c55e'
        ctx.lineWidth = 2
        ctx.setLineDash([8, 4])
        ctx.strokeRect(b.x * w, b.y * h, b.width * w, b.height * h)
        ctx.setLineDash([])
      }

      // Draw regions (relative to boundary)
      const boundary = this.currentSampleBoundary || { x: 0, y: 0, width: 1, height: 1 }

      this.regions.forEach((region, idx) => {
        const bx = boundary.x * w
        const by = boundary.y * h
        const bw = boundary.width * w
        const bh = boundary.height * h

        const rx = bx + region.x * bw
        const ry = by + region.y * bh
        const rw = region.width * bw
        const rh = region.height * bh

        const color = this.regionColor(idx)

        ctx.fillStyle = color + '25'
        ctx.fillRect(rx, ry, rw, rh)
        ctx.strokeStyle = color
        ctx.lineWidth = idx === this.selectedRegionIdx ? 3 : 2
        ctx.strokeRect(rx, ry, rw, rh)

        if (region.label) {
          ctx.fillStyle = color
          ctx.font = 'bold 12px sans-serif'
          ctx.fillText(region.label, rx + 4, ry - 4 > 12 ? ry - 4 : ry + 14)
        }
      })

      // Draw in-progress rectangle
      if (this.drawing && this.drawStart && this.drawCurrent) {
        const x = Math.min(this.drawStart.x, this.drawCurrent.x)
        const y = Math.min(this.drawStart.y, this.drawCurrent.y)
        const rw = Math.abs(this.drawCurrent.x - this.drawStart.x)
        const rh = Math.abs(this.drawCurrent.y - this.drawStart.y)

        const color = this.regionColor(this.regions.length)
        ctx.fillStyle = color + '25'
        ctx.fillRect(x, y, rw, rh)
        ctx.strokeStyle = color
        ctx.lineWidth = 2
        ctx.strokeRect(x, y, rw, rh)
      }
    },

    getCanvasPos(e) {
      const canvas = this.$refs.overlayCanvas
      const rect = canvas.getBoundingClientRect()
      return {
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      }
    },

    onCanvasMouseDown(e) {
      this.drawing = true
      this.drawStart = this.getCanvasPos(e)
      this.drawCurrent = this.drawStart
    },

    onCanvasMouseMove(e) {
      if (!this.drawing) return
      this.drawCurrent = this.getCanvasPos(e)
      this.redrawCanvas()
    },

    onCanvasMouseUp(e) {
      if (!this.drawing) return
      this.drawing = false

      const canvas = this.$refs.overlayCanvas
      const w = canvas.width
      const h = canvas.height
      const end = this.getCanvasPos(e)

      const x = Math.min(this.drawStart.x, end.x)
      const y = Math.min(this.drawStart.y, end.y)
      const rw = Math.abs(end.x - this.drawStart.x)
      const rh = Math.abs(end.y - this.drawStart.y)

      if (rw < 5 || rh < 5) {
        this.drawStart = null
        this.drawCurrent = null
        this.redrawCanvas()
        return
      }

      const boundary = this.currentSampleBoundary || { x: 0, y: 0, width: 1, height: 1 }
      const bx = boundary.x * w
      const by = boundary.y * h
      const bw = boundary.width * w
      const bh = boundary.height * h

      this.pendingLabel = {
        label: '',
        region: {
          x: (x - bx) / bw,
          y: (y - by) / bh,
          width: rw / bw,
          height: rh / bh,
        },
      }

      this.$nextTick(() => {
        if (this.$refs.labelInput) this.$refs.labelInput.focus()
      })

      this.drawStart = null
      this.drawCurrent = null
      this.redrawCanvas()
    },

    confirmLabel() {
      if (!this.pendingLabel || !this.pendingLabel.label) return
      this.regions.push({
        label: this.pendingLabel.label,
        ...this.pendingLabel.region,
      })
      this.pendingLabel = null
      this.redrawCanvas()
    },

    cancelLabel() {
      this.pendingLabel = null
      this.redrawCanvas()
    },

    async uploadPendingSamples() {
      for (let i = 0; i < this.samples.length; i++) {
        const sample = this.samples[i]
        if (!sample._file) continue

        const mediaData = await this.uploadWithReplace(sample._file, `ocr-models/${this.model.uuid}`)
        if (!mediaData) continue

        const imageKey = mediaData.key || mediaData.Key

        const sampleResp = await this.$store.dispatch('ocrModels/addSample', {
          modelUuid: this.model.uuid,
          image_key: imageKey,
          image_url: mediaData.local_url || mediaData.public_url || '',
          is_primary: sample.is_primary,
          document_boundary: sample.document_boundary || undefined,
        })

        this.$set(this.samples, i, { ...sampleResp, _localUrl: sample._localUrl })
      }
    },

    async saveModel() {
      this.saving = true
      try {
        const config = {}
        if (this.form.psm != null) config.psm = Number(this.form.psm)

        const payload = {
          name: this.form.name,
          description: this.form.description,
          engine: this.form.engine,
          language: this.form.language,
          config: Object.keys(config).length ? config : null,
          regions: this.regions.map(r => ({
            label: r.label,
            x: r.x,
            y: r.y,
            width: r.width,
            height: r.height,
          })),
        }

        if (this.model) {
          await this.$store.dispatch('ocrModels/updateModel', {
            uuid: this.model.uuid,
            ...payload,
          })
        } else {
          const created = await this.$store.dispatch('ocrModels/createModel', payload)
          this.model = created
          this.$router.replace(`/integrations/page/protokol-media/ocr-editor?uuid=${created.uuid}`)
        }

        await this.uploadPendingSamples()
      } catch (e) {
        console.error('Failed to save model', e)
      } finally {
        this.saving = false
      }
    },
  },

  watch: {
    activeSampleIdx() {
      this.$nextTick(() => {
        if (this.drawMode === 'boundary') {
          this.initCropper()
        } else {
          this.resizeCanvas()
        }
      })
    },
    drawMode(newMode) {
      this.$nextTick(() => {
        if (newMode === 'boundary') {
          this.initCropper()
        } else {
          this.destroyCropper()
          this.resizeCanvas()
        }
      })
    },
    regions: {
      deep: true,
      handler() {
        if (this.drawMode === 'region') {
          this.redrawCanvas()
        }
      },
    },
  },
}
</script>

<style scoped lang="scss">
@import "@/assets/scss/_custom.scss";

.ocr-editor {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 100%;
}

.ocr-editor-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 24px;
  border-bottom: 1px solid var(--border-default);
  flex-shrink: 0;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 16px;
}

.back-link {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 10px;
  color: var(--text-muted);
  background: var(--bg-card);
  border: 1px solid var(--border-default);
  text-decoration: none;
  transition: all 0.15s ease;

  &:hover {
    color: var(--text-primary);
    border-color: var(--border-hover);
  }
}

.header-title {
  font-size: 1.2rem;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0;
}

.btn-save {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 20px;
  background: linear-gradient(135deg, #10b981, #34d399);
  color: #fff;
  border: none;
  border-radius: 10px;
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.ocr-editor-body {
  display: flex;
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

/* Left Panel */
.left-panel {
  width: 300px;
  flex-shrink: 0;
  border-right: 1px solid var(--border-default);
  padding: 20px;
  overflow-y: auto;
}

.panel-section {
  margin-bottom: 20px;
}

.section-label {
  display: block;
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--text-muted);
  margin-bottom: 6px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.form-input {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid var(--border-default);
  border-radius: 8px;
  background: var(--bg-main);
  color: var(--text-primary);
  font-size: 0.9rem;
  outline: none;

  &:focus {
    border-color: #10b981;
  }
}

.engine-gate {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 10px;
  margin-top: 10px;
  padding: 12px 14px;
  border-radius: 10px;
  background: rgba(234, 179, 8, 0.08);
  border: 1px solid rgba(234, 179, 8, 0.25);

  > i {
    color: #eab308;
    font-size: 1rem;
  }
}

.engine-gate-text {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.engine-gate-title {
  font-size: 0.82rem;
  font-weight: 600;
  color: var(--text-primary);
}

.engine-gate-desc {
  font-size: 0.75rem;
  color: var(--text-muted);
  line-height: 1.4;
}

.engine-gate-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  border-radius: 8px;
  background: linear-gradient(135deg, #eab308, #f59e0b);
  color: #fff;
  font-size: 0.8rem;
  font-weight: 600;
  text-decoration: none;
  transition: opacity 0.15s ease;

  &:hover {
    opacity: 0.9;
    color: #fff;
    text-decoration: none;
  }
}

.lang-select {
  .vs__dropdown-toggle {
    background: var(--bg-main);
    border: 1px solid var(--border-default);
    border-radius: 8px;
    padding: 4px 8px;
  }
  .vs__search, .vs__selected {
    color: var(--text-primary);
    font-size: 0.9rem;
  }
  .vs__clear, .vs__open-indicator {
    fill: var(--text-secondary);
  }
  .vs__dropdown-menu {
    background: var(--bg-card);
    border: 1px solid var(--border-default);
    border-radius: 8px;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  }
  .vs__dropdown-option {
    color: var(--text-primary);
    font-size: 0.85rem;
  }
  .vs__dropdown-option--highlight {
    background: rgba(16, 185, 129, 0.15);
    color: #10b981;
  }
}

.psm-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.psm-option {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 7px 10px;
  border-radius: 8px;
  border: 1px solid transparent;
  cursor: pointer;
  transition: all 0.15s ease;

  input[type="radio"] {
    margin-top: 2px;
    accent-color: #10b981;
  }

  &.active {
    background: rgba(16, 185, 129, 0.06);
    border-color: rgba(16, 185, 129, 0.25);
  }

  &:hover {
    background: var(--bg-main);
  }
}

.psm-option-content {
  display: flex;
  flex-direction: column;
  gap: 1px;
}

.psm-option-name {
  font-size: 0.82rem;
  font-weight: 600;
  color: var(--text-primary);
}

.psm-option-desc {
  font-size: 0.7rem;
  color: var(--text-muted);
  line-height: 1.35;
}

.mode-toggle {
  display: flex;
  gap: 4px;
  background: var(--bg-main);
  border-radius: 8px;
  padding: 3px;
}

.mode-btn {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 6px 10px;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: var(--text-muted);
  font-size: 0.8rem;
  cursor: pointer;
  transition: all 0.15s ease;

  &.active {
    background: var(--bg-card);
    color: var(--text-primary);
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
}

/* Samples */
.samples-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.sample-thumb {
  position: relative;
  width: 60px;
  height: 60px;
  border-radius: 8px;
  overflow: hidden;
  border: 2px solid var(--border-default);
  cursor: pointer;
  transition: border-color 0.15s ease;

  &.active {
    border-color: #10b981;
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

.primary-badge {
  position: absolute;
  top: 2px;
  left: 2px;
  color: #eab308;
  font-size: 10px;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.5);
}

.sample-remove {
  position: absolute;
  top: 2px;
  right: 2px;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.6);
  color: #fff;
  border: none;
  font-size: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.15s ease;
}

.sample-thumb:hover .sample-remove {
  opacity: 1;
}

.sample-add {
  width: 60px;
  height: 60px;
  border-radius: 8px;
  border: 2px dashed var(--border-default);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-muted);
  cursor: pointer;
  transition: border-color 0.15s ease;

  &:hover {
    border-color: #10b981;
    color: #10b981;
  }
}

/* Regions */
.regions-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.region-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 8px;
  border-radius: 6px;
  border: 1px solid transparent;
  cursor: pointer;
  transition: all 0.15s ease;

  &.active, &:hover {
    background: var(--bg-main);
    border-color: var(--border-default);
  }
}

.region-color {
  width: 12px;
  height: 12px;
  border-radius: 3px;
  flex-shrink: 0;
}

.region-label-input {
  flex: 1;
  border: none;
  background: transparent;
  color: var(--text-primary);
  font-size: 0.85rem;
  outline: none;
  padding: 2px 0;
}

.region-remove {
  border: none;
  background: none;
  color: var(--text-muted);
  cursor: pointer;
  font-size: 0.75rem;
  opacity: 0;
  transition: opacity 0.15s ease;
}

.region-item:hover .region-remove {
  opacity: 1;
}

/* Canvas Area */
.canvas-area {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  overflow: auto;
  background: var(--bg-main);
}

.canvas-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
}

.upload-zone {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 60px 80px;
  border: 2px dashed var(--border-default);
  border-radius: 16px;
  color: var(--text-muted);
  cursor: pointer;
  transition: border-color 0.15s ease;

  &:hover {
    border-color: #10b981;
    color: #10b981;
  }

  i {
    font-size: 2.5rem;
  }

  span {
    font-size: 0.95rem;
  }
}

.canvas-wrapper {
  position: relative;
  display: inline-block;
  max-width: 100%;
  max-height: 100%;
}

.sample-img {
  display: block;
  max-width: 100%;
  max-height: calc(100vh - 200px);
  border-radius: 8px;
  user-select: none;
}

.overlay-canvas {
  position: absolute;
  top: 0;
  left: 0;
  cursor: crosshair;
  border-radius: 8px;
}

/* Cropper.js green boundary theme */
::v-deep .cropper-view-box {
  outline: 2px dashed #22c55e;
  outline-color: rgba(34, 197, 94, 0.75);
}

::v-deep .cropper-line {
  background-color: #22c55e;
}

::v-deep .cropper-point {
  background-color: #22c55e;
  width: 8px;
  height: 8px;
}

/* Dialogs */
.dialog-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.dialog {
  background: var(--bg-card);
  border: 1px solid var(--border-default);
  border-radius: 16px;
  padding: 24px;
  width: 360px;
  max-width: 90vw;
}

.dialog-small {
  width: 320px;
}

.dialog-title {
  font-size: 1.1rem;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0 0 16px;
}

.dialog-field {
  margin-bottom: 16px;

  input {
    width: 100%;
    padding: 10px 14px;
    border: 1px solid var(--border-default);
    border-radius: 8px;
    background: var(--bg-main);
    color: var(--text-primary);
    font-size: 0.9rem;
    outline: none;

    &:focus {
      border-color: #10b981;
    }
  }
}

.dialog-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.btn-cancel {
  padding: 8px 18px;
  border: 1px solid var(--border-default);
  border-radius: 8px;
  background: transparent;
  color: var(--text-muted);
  cursor: pointer;
  font-size: 0.9rem;
}

.btn-confirm {
  padding: 8px 18px;
  border: none;
  border-radius: 8px;
  background: linear-gradient(135deg, #10b981, #34d399);
  color: #fff;
  font-weight: 600;
  cursor: pointer;
  font-size: 0.9rem;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}
</style>
